import { get, patch, post } from '@/api';

/**
 * Board-based checkout (PIX-456). Pricing is flat per (board × billing
 * cycle); the All Board Bundle is the row with a null board. There is no
 * subject selection anywhere - a purchase covers every subject on the board.
 */
export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    billingCycles: [],
    pricingTiers: [], // full matrix: every board × cycle, plus bundle rows
    selectedBoardUuid: null, // null + bundle=false → nothing picked yet
    isBundleSelected: false,
    selectedBillingCycleId: null,
    voucherCode: '',
    voucherResult: null,
    featuredVoucher: null, // { code, discount_type, discount_value } or null
    checkoutPreview: null,
    previewLoading: false,
    catalogLoading: false,
    pricingRequestId: 0,
    previewRequestId: 0,
    countryRequestId: 0,
    mySubscriptions: [],
    // Billing details (PIX-493)
    billingCountry: null,
    billingState: null,
    billingAddress: '',
    billingGstin: '',
  }),

  getters: {
    /** Boards in display order, derived from the tier matrix. */
    boards: (state) => {
      const seen = new Map();
      for (const tier of state.pricingTiers) {
        if (tier.is_bundle || seen.has(tier.board_uuid)) continue;
        seen.set(tier.board_uuid, {
          uuid: tier.board_uuid,
          name: tier.board_name,
        });
      }
      return [...seen.values()];
    },

    hasSelection: (state) =>
      state.isBundleSelected || Boolean(state.selectedBoardUuid),

    selectionLabel(state) {
      if (state.isBundleSelected) return 'All Board Bundle';
      return (
        this.boards.find((b) => b.uuid === state.selectedBoardUuid)?.name || ''
      );
    },

    selectedBillingCycle: (state) =>
      state.billingCycles.find(
        (c) => c.uuid === state.selectedBillingCycleId
      ) ?? null,

    /** Tier rows for the current board/bundle selection. */
    selectionTiers: (state) =>
      state.pricingTiers.filter((t) =>
        state.isBundleSelected
          ? t.is_bundle
          : !t.is_bundle && t.board_uuid === state.selectedBoardUuid
      ),

    /**
     * One card per billing cycle for the current selection: total price,
     * per-month equivalent, % saved vs paying the same months monthly, and
     * currency (local if CountryPricing exists, else USD).
     */
    planCards(state) {
      const tiers = this.selectionTiers;
      if (!tiers.length) return [];

      const getPrice = (tier) => parseFloat(tier.local_price ?? tier.price_usd);
      // All tiers for a given country share the same currency.
      const currency =
        tiers.find((t) => t.local_currency)?.local_currency ?? 'USD';

      const monthlyTier = tiers.find((t) => t.interval_months === 1);
      const monthlyPrice = monthlyTier ? getPrice(monthlyTier) : null;

      return [...tiers]
        .sort((a, b) => a.interval_months - b.interval_months)
        .map((tier, index, sorted) => {
          const price = getPrice(tier);
          const perMonth = price / tier.interval_months;
          let savePct = null;
          if (monthlyPrice && tier.interval_months > 1) {
            savePct = Math.round((1 - perMonth / monthlyPrice) * 100);
          }
          return {
            cycleUuid: tier.billing_cycle_uuid,
            cycleName: tier.billing_cycle_name,
            intervalMonths: tier.interval_months,
            price,
            currency,
            perMonth: Math.round(perMonth * 100) / 100,
            savePct: savePct && savePct > 0 ? savePct : null,
            isBestValue: index === sorted.length - 1 && sorted.length > 1,
          };
        });
    },
  },

  actions: {
    async fetchPricingTiers(country) {
      const requestId = ++this.pricingRequestId;
      const params = country ? `?country=${country}` : '';
      const { data, status } = await get(
        `api/subscriptions/pricing-tiers/${params}`
      );
      if (requestId === this.pricingRequestId && status === 200) {
        this.pricingTiers = data;
      }
    },

    async fetchCatalog() {
      this.catalogLoading = true;
      try {
        const [, cyclesRes] = await Promise.all([
          this.fetchPricingTiers(this.billingCountry),
          get('api/subscriptions/billing-cycles/'),
        ]);
        if (cyclesRes.status === 200) {
          this.billingCycles = cyclesRes.data;
          // Figma default: the 1 Year plan is pre-selected.
          if (!this.selectedBillingCycleId) {
            const annual = cyclesRes.data.find((c) => c.name === 'annual');
            this.selectedBillingCycleId =
              annual?.uuid ?? cyclesRes.data[0]?.uuid ?? null;
          }
        }
      } finally {
        this.catalogLoading = false;
      }
    },

    selectBoard(uuid) {
      this.selectedBoardUuid = uuid;
      this.isBundleSelected = false;
      this.fetchCheckoutPreview();
    },

    selectBundle() {
      this.isBundleSelected = true;
      this.selectedBoardUuid = null;
      this.fetchCheckoutPreview();
    },

    setBillingCycle(uuid) {
      this.selectedBillingCycleId = uuid;
      this.fetchCheckoutPreview();
    },

    async validateVoucher(code) {
      if (!code) {
        this.voucherResult = null;
        return;
      }
      try {
        const { data, status } = await post(
          'api/subscriptions/voucher/validate/',
          { code }
        );
        if (status === 200) {
          this.voucherResult = data;
        } else {
          this.voucherResult = { is_valid: false, detail: data?.detail };
        }
      } catch {
        this.voucherResult = { is_valid: false, detail: 'Invalid voucher.' };
      }
      this.fetchCheckoutPreview();
    },

    // The code checkout suggests under the voucher input. Anything other than
    // a clean 200 (older backend, network failure) just means no suggestion.
    async fetchFeaturedVoucher() {
      try {
        const res = await get('api/subscriptions/voucher/featured/');
        this.featuredVoucher =
          res?.status === 200 ? (res.data?.featured ?? null) : null;
      } catch {
        this.featuredVoucher = null;
      }
    },

    clearVoucher() {
      this.voucherCode = '';
      this.voucherResult = null;
      this.fetchCheckoutPreview();
    },

    async setCountry(code) {
      const requestId = ++this.countryRequestId;
      this.billingCountry = code || null;
      this.billingState = null;
      this.catalogLoading = true;
      try {
        await Promise.all([
          this.fetchPricingTiers(this.billingCountry),
          this.fetchCheckoutPreview(),
        ]);
      } finally {
        if (requestId === this.countryRequestId) {
          this.catalogLoading = false;
        }
      }
    },

    setBillingState(state) {
      this.billingState = state || null;
    },

    setBillingAddress(addr) {
      this.billingAddress = addr || '';
    },

    setBillingGstin(gstin) {
      this.billingGstin = (gstin || '').toUpperCase();
    },

    _cartBody() {
      return {
        ...(this.isBundleSelected
          ? { bundle: true }
          : { board_uuid: this.selectedBoardUuid }),
        billing_cycle_uuid: this.selectedBillingCycleId,
        ...(this.voucherResult?.is_valid
          ? { voucher_code: this.voucherCode }
          : {}),
        ...(this.billingCountry ? { country: this.billingCountry } : {}),
        ...(this.billingState ? { state: this.billingState } : {}),
      };
    },

    async fetchCheckoutPreview() {
      if (!this.selectedBillingCycleId || !this.hasSelection) {
        this.checkoutPreview = null;
        return;
      }
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;
      try {
        const { data, status } = await post(
          'api/subscriptions/checkout-preview/',
          this._cartBody()
        );
        if (requestId === this.previewRequestId && status === 200) {
          this.checkoutPreview = data;
        }
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async fetchMySubscriptions() {
      const { data, status } = await get('api/subscriptions/my-subscriptions/');
      if (status === 200) {
        this.mySubscriptions = data;
      }
    },

    // Ask Razorpay/PayPal about payments still pending on our side (students
    // often leave checkout before it confirms). Returns the server's summary,
    // or null when the check was skipped (throttled, offline, gateway error).
    async reconcilePayments() {
      try {
        const response = await post('api/subscriptions/reconcile-payments/');
        return response?.status === 200 ? response.data : null;
      } catch (e) {
        return null;
      }
    },

    async updateTrialSelection({ boardUuid = null, bundle = false }) {
      const response = await patch(
        'api/subscriptions/trial-selection/',
        bundle ? { bundle: true } : { board_uuid: boardUuid }
      );
      if (response?.status === 200) {
        const updated = response.data;
        const index = this.mySubscriptions.findIndex(
          (subscription) => subscription.uuid === updated.uuid
        );
        if (index >= 0) this.mySubscriptions[index] = updated;
        else this.mySubscriptions.unshift(updated);
      }
      return response;
    },

    async createOrder() {
      return await post('api/subscriptions/create-order/', {
        ...this._cartBody(),
        country: this.billingCountry,
        address: this.billingAddress,
        ...(this.billingCountry === 'IN' && this.billingState
          ? { state: this.billingState }
          : {}),
        ...(this.billingCountry === 'IN' && this.billingGstin
          ? { gstin: this.billingGstin }
          : {}),
      });
    },

    // The cart lives on the Payment since PIX-452 - verification only sends
    // the gateway callback fields.
    async verifyPayment(payload) {
      return await post('api/subscriptions/verify-payment/', payload);
    },

    async fetchDocuments(paymentUuid) {
      const { data, status } = await get(
        `api/subscriptions/documents/?payment_uuid=${paymentUuid}`
      );
      if (status === 200) return data;
      return [];
    },

    async fetchDownloadUrl(docUuid) {
      const { data, status } = await get(
        `api/subscriptions/documents/${docUuid}/download/`
      );
      if (status === 200) return data.url;
      return null;
    },

    resetCart() {
      this.selectedBoardUuid = null;
      this.isBundleSelected = false;
      this.selectedBillingCycleId = null;
      this.voucherCode = '';
      this.voucherResult = null;
      this.checkoutPreview = null;
      this.billingCountry = null;
      this.billingState = null;
      this.billingAddress = '';
      this.billingGstin = '';
    },
  },
});
